/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  LinkedLogo,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import { useConfiguration } from '../../../../context/configurationContext';
import logoImg from '../../../../assets/Frame.png';
import IconLogo from '../../../../components/IconLogo/IconLogo';
const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    customLinks,
    onLogout,
    onClose,
  } = props;

  const user = ensureCurrentUser(currentUser);

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const displayName = user.attributes.profile.firstName;

  const inboxTab = currentUserHasListings ? 'sales' : 'orders';
  const [isOpen, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const openNavbar = e => {
    e.preventDefault();
    setOpen(true);
  };
  const closeNavbar = e => {
    setOpen(false);
  };

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;
  const config = useConfiguration();
  const marketplaceName = config.marketplaceName;
  const intl = useIntl();

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={classNames(css.inbox, currentPageClass(`LoginPage`))}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.accountLinksWrapper}>
            <div className={css.closeIcon} onClick={onClose}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="34"
                height="34"
                viewBox="0 0 34 34"
                fill="none"
              >
                <path d="M2 2L32 32M32 2L2 32" stroke="#A645D6" stroke-width="4"></path>
              </svg>
            </div>
            <NamedLink
              className={classNames(css.inbox, currentPageClass(`LandingPage`))}
              name="LandingPage"
              params={{ tab: inboxTab }}
            >
              <FormattedMessage id="Topbar.ModelOpen.home" />
              {notificationCountBadge}
            </NamedLink>
            <NamedLink
              className={classNames(css.inbox, currentPageClass(`SearchPage`))}
              name="SearchPage"
              params={{ tab: inboxTab }}
            >
              <FormattedMessage id="Topbar.ModelOpen.buyValue" />
              {notificationCountBadge}
            </NamedLink>
            <NamedLink
              className={classNames(css.inbox, currentPageClass(`ManageListingsPage`))}
              name="ManageListingsPage"
              params={{ tab: inboxTab }}
            >
              <FormattedMessage id="Topbar.ModelOpen.sellValue" />
              {notificationCountBadge}
            </NamedLink>
            <NamedLink
              className={classNames(css.inbox, currentPageClass(`learn`))}
              name="learn"
              params={{ tab: inboxTab }}
            >
              <FormattedMessage id="Topbar.ModelOpen.learnValue" />
              {notificationCountBadge}
            </NamedLink>
            {/* <div className={css.signuplink}>{signup}</div> */}
            <div>{login}</div>
            {/* <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            /> */}
          </div>

          <div className={css.customLinksWrapper}>{extraLinks}</div>

          <div className={css.spacer} />
        </div>
        {/* <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div> */}
      </div>
    );
  }

  return (
    <>
      <div className={css.root}>
        {/* <AvatarLarge className={css.avatar} user={currentUser} /> */}

        <div className={css.headerNav}>
          <div className={css.profileIcon}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
            >
              <g id="Frame" clip-path="url(#clip0_2558_120)">
                <g id="Group">
                  <path
                    id="Vector"
                    d="M20 40C31.0457 40 40 31.0457 40 20C40 8.9543 31.0457 0 20 0C8.9543 0 0 8.9543 0 20C0 31.0457 8.9543 40 20 40Z"
                    fill="#58D1D6"
                  />
                  <path
                    id="Vector_2"
                    d="M29.4975 11.1181C29.4975 16.2463 25.3334 20.4104 20.2051 20.4104C15.0769 20.4104 10.9128 16.2463 10.9128 11.1181C10.9128 5.98986 15.0769 1.82576 20.2051 1.82576C25.3334 1.80525 29.4975 5.96935 29.4975 11.1181Z"
                    fill="#9045D6"
                  />
                  <g id="Group_2">
                    <path
                      id="Vector_3"
                      d="M10.7898 20.3692L10.8103 20.3897C12.0206 21.6 13.9693 21.6 15.159 20.3897L18.1129 17.4359C19.3231 16.2256 19.3231 14.2769 18.1129 13.0872L18.0924 13.0667L10.7898 20.3692Z"
                      fill="#9045D6"
                    />
                    <path
                      id="Vector_4"
                      d="M14.6257 24.2667C14.6462 24.2667 14.6462 24.2667 14.6668 24.2667C16.3693 24.2667 17.7437 22.8923 17.7437 21.1898V17.0257C17.7437 15.3231 16.3693 13.9487 14.6668 13.9487C14.6462 13.9487 14.6462 13.9487 14.6257 13.9487V24.2667Z"
                      fill="#9045D6"
                    />
                  </g>
                  <path
                    id="Vector_5"
                    d="M29.6819 20.3692L29.6614 20.3897C28.4512 21.6 26.5025 21.6 25.3127 20.3897L22.3589 17.4359C21.1486 16.2256 21.1486 14.2769 22.3589 13.0872L22.3794 13.0667L29.6819 20.3692Z"
                    fill="#9045D6"
                  />
                  <path
                    id="Vector_6"
                    d="M30.8104 17.2923L30.7898 17.3128C29.5796 18.5231 27.6309 18.5231 26.4411 17.3128L23.4873 14.359C22.277 13.1487 22.277 11.2 23.4873 10.0103L23.5078 9.98975L30.8104 17.2923Z"
                    fill="#9045D6"
                  />
                  <path
                    id="Vector_7"
                    d="M9.70264 17.2923L9.72315 17.3128C10.9334 18.5231 12.8821 18.5231 14.0719 17.3128L17.0462 14.359C18.2565 13.1487 18.2565 11.2 17.0462 10.0103L17.0257 9.98975L9.70264 17.2923Z"
                    fill="#9045D6"
                  />
                  <path
                    id="Vector_8"
                    d="M25.8462 24.2667C25.8257 24.2667 25.8257 24.2667 25.8052 24.2667C24.1026 24.2667 22.7283 22.8923 22.7283 21.1898V17.0257C22.7283 15.3231 24.1026 13.9487 25.8052 13.9487C25.8257 13.9487 25.8257 13.9487 25.8462 13.9487V24.2667Z"
                    fill="#9045D6"
                  />
                  <g id="Group_3">
                    <path
                      id="Vector_9"
                      d="M14.5026 14.5641C15.0564 15.9179 14.8103 17.3128 13.9282 17.6615C13.0462 18.0307 11.8769 17.2307 11.3231 15.8769C10.7692 14.523 11.0154 13.1282 11.8975 12.7794C12.759 12.4307 13.9282 13.2307 14.5026 14.5641Z"
                      fill="#FFEFCF"
                    />
                    <path
                      id="Vector_10"
                      d="M25.9898 14.5641C25.436 15.9179 25.6821 17.3128 26.5642 17.6615C27.4463 18.0307 28.6155 17.2307 29.1693 15.8769C29.7232 14.523 29.477 13.1282 28.595 12.7794C27.7334 12.4307 26.5642 13.2307 25.9898 14.5641Z"
                      fill="#FFEFCF"
                    />
                  </g>
                  <path
                    id="Vector_11"
                    d="M20 39.9999C26.0923 39.9999 31.5282 37.2717 35.2 32.9845C33.4359 30.0101 27.3641 28.2255 23.4666 26.5024H16.9641C12.3282 28.5947 6.46152 30.2153 5.08716 33.3332C8.75895 37.4153 14.0718 39.9999 20 39.9999Z"
                    fill="#1A1887"
                  />
                  <path
                    id="Vector_12"
                    d="M17.0667 28.5538C17.0667 30.2974 18.482 31.7333 20.2461 31.7333C21.9897 31.7333 23.4256 30.318 23.4256 28.5538V21.1692C23.4256 19.4256 22.0102 17.9897 20.2461 17.9897C18.5025 17.9897 17.0667 19.4051 17.0667 21.1692V28.5538Z"
                    fill="#FFEFCF"
                  />
                  <path
                    id="Vector_13"
                    opacity="0.8"
                    d="M20.2461 18.9949C21.9897 18.9949 23.4256 20.4103 23.4256 22.1744V22.6256C22.0512 24.0821 20.2461 24.9641 18.2564 24.9641C17.8461 24.9641 17.4564 24.9231 17.0871 24.8615V22.1744C17.0666 20.4103 18.5025 18.9949 20.2461 18.9949Z"
                    fill="#FFEFCF"
                  />
                  <path
                    id="Vector_14"
                    d="M27.9794 13.9897C27.9794 19.0154 24.5128 22.9539 20.2461 22.9539C15.9794 22.9539 12.5128 19.0154 12.5128 13.9897C12.5128 8.96411 11.8358 2.97437 20.2461 2.97437C28.6564 2.97437 27.9794 8.94359 27.9794 13.9897Z"
                    fill="#FFEFCF"
                  />
                  <path
                    id="Vector_15"
                    d="M17.4768 27.6309L20.3691 31.6924L23.1589 27.6309L20.5742 39.3847L17.4768 27.6309Z"
                    fill="#1A1887"
                  />
                  <path
                    id="Vector_16"
                    d="M12.718 28.3693L15.3026 24.8616H17.477L20.4924 39.1385L12.718 28.3693Z"
                    fill="#1A1887"
                  />
                  <path
                    id="Vector_17"
                    d="M27.795 28.2667L25.1283 24.8616H23.0565L20.5745 39.3847L27.795 28.2667Z"
                    fill="#1A1887"
                  />
                  <path
                    id="Vector_18"
                    d="M25.8872 7.03589C25.8872 7.03589 27.6718 10.5641 27.8975 12.1231C28.1641 9.49743 25.8872 7.03589 25.8872 7.03589Z"
                    fill="#FFEFCF"
                  />
                  <path
                    id="Vector_19"
                    d="M18.3179 18.4819C18.3179 18.4819 19.1384 19.4255 20.2871 19.4255C21.4358 19.4255 22.2563 18.4819 22.2563 18.4819H18.3179Z"
                    fill="#FFEFCF"
                  />
                  <path
                    id="Vector_20"
                    d="M11.7539 13.7436C11.7744 13.7436 11.7744 13.7436 11.7949 13.7436C13.4975 13.7436 14.8719 12.3692 14.8719 10.6667V4.45127C14.8719 3.6923 13.1078 5.41538 11.7949 7.67179C11.7949 7.67179 11.7744 7.71281 11.7539 7.71281V13.7436Z"
                    fill="#9045D6"
                  />
                  <path
                    id="Vector_21"
                    d="M13.3948 5.94871V9.80512C13.4153 9.80512 13.4153 9.80512 13.4358 9.80512C15.2614 9.80512 16.7589 8.3282 16.7589 6.48204V3.03589C15.0563 3.65127 14.0307 4.67691 13.3948 5.94871Z"
                    fill="#9045D6"
                  />
                  <path
                    id="Vector_22"
                    d="M28.4102 13.7437C28.3897 13.7437 28.3897 13.7437 28.3692 13.7437C26.6666 13.7437 25.2922 12.3693 25.2922 10.6667V4.06162C25.2922 3.30264 27.0563 5.39495 28.3692 7.65136C28.3692 7.65136 28.3897 7.69239 28.4102 7.69239V13.7437Z"
                    fill="#9045D6"
                  />
                  <path
                    id="Vector_23"
                    d="M24.4308 4.96414C22.5642 4.96414 20.9847 3.63081 20.6359 1.84619H19.6513C17.518 1.84619 15.7949 3.56927 15.7949 5.7026C15.7949 7.83593 17.518 9.55901 19.6513 9.55901H26.2565C28.3898 9.55901 30.1129 7.83593 30.1129 5.7026C30.1129 5.43593 30.0924 5.18978 30.0308 4.94363H24.4308V4.96414Z"
                    fill="#9045D6"
                  />
                  <path
                    id="Vector_24"
                    d="M25.9691 4.73857C25.7845 5.55908 24.4102 5.96934 22.8512 5.64113C21.3127 5.31293 20.205 4.38985 20.3691 3.54882C20.5538 2.72831 21.9281 2.31805 23.4871 2.64626C25.0256 2.97446 26.1332 3.91805 25.9691 4.73857Z"
                    fill="#9045D6"
                  />
                </g>
              </g>
              <defs>
                <clipPath id="clip0_2558_120">
                  <rect width="40" height="40" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </div>
          <div className={css.userName}>
            {
              <p>
                {currentUser?.attributes?.profile?.firstName}{' '}
                {currentUser?.attributes?.profile?.lastName}
              </p>
            }
          </div>

          <div className={css.closeIcon} onClick={onClose}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="34"
              height="34"
              viewBox="0 0 34 34"
              fill="none"
            >
              <path d="M2 2L32 32M32 2L2 32" stroke="#A645D6" stroke-width="4"></path>
            </svg>
          </div>
        </div>
        <div className={css.content}>
          <div className={css.accountLinksWrapper}>
            <NamedLink
              className={classNames(css.inbox, currentPageClass(`LandingPage`))}
              name="LandingPage"
              params={{ tab: inboxTab }}
            >
              <FormattedMessage id="Topbar.ModelOpen.home" />
              {notificationCountBadge}
            </NamedLink>
            <NamedLink
              className={classNames(css.inbox, currentPageClass(`SearchPage`))}
              name="SearchPage"
              params={{ tab: inboxTab }}
            >
              <FormattedMessage id="Topbar.ModelOpen.buyValue" />
              {notificationCountBadge}
            </NamedLink>
            <NamedLink
              className={classNames(css.inbox, currentPageClass(`ManageListingsPage`))}
              name="ManageListingsPage"
              params={{ tab: inboxTab }}
            >
              <FormattedMessage id="Topbar.ModelOpen.sellValue" />
              {notificationCountBadge}
            </NamedLink>
            <NamedLink
              className={classNames(css.inbox, currentPageClass(`learn`))}
              name="learn"
              params={{ tab: inboxTab }}
            >
              <FormattedMessage id="Topbar.ModelOpen.learnValue" />
              {notificationCountBadge}
            </NamedLink>
            {/* <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
              name="ProfileSettingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
            </NamedLink>
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
            </NamedLink> */}

            <NamedLink
              className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
              name="InboxPage"
              params={{ tab: inboxTab }}
            >
              <FormattedMessage id="TopbarMobileMenu.inboxLink" />
              {notificationCountBadge}
            </NamedLink>

            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <FormattedMessage id="TopbarMobileMenu.logoutLink" />
            </InlineTextButton>
          </div>
          <div className={css.customLinksWrapper}>{extraLinks}</div>
          <div className={css.spacer} />
        </div>
        {/* <div className={css.footer}>
        <NamedLink className={css.createNewListingLink} name="NewListingPage">
          <FormattedMessage id="TopbarMobileMenu.newListingLink" />
        </NamedLink>
      </div> */}
      </div>

      {!isAuthenticated && (
        <div className={css.side_nav}>
          <div className={css.headerNav}>
            <div className={css.profileIcon}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
              >
                <g id="Frame" clip-path="url(#clip0_2558_120)">
                  <g id="Group">
                    <path
                      id="Vector"
                      d="M20 40C31.0457 40 40 31.0457 40 20C40 8.9543 31.0457 0 20 0C8.9543 0 0 8.9543 0 20C0 31.0457 8.9543 40 20 40Z"
                      fill="#58D1D6"
                    />
                    <path
                      id="Vector_2"
                      d="M29.4975 11.1181C29.4975 16.2463 25.3334 20.4104 20.2051 20.4104C15.0769 20.4104 10.9128 16.2463 10.9128 11.1181C10.9128 5.98986 15.0769 1.82576 20.2051 1.82576C25.3334 1.80525 29.4975 5.96935 29.4975 11.1181Z"
                      fill="#9045D6"
                    />
                    <g id="Group_2">
                      <path
                        id="Vector_3"
                        d="M10.7898 20.3692L10.8103 20.3897C12.0206 21.6 13.9693 21.6 15.159 20.3897L18.1129 17.4359C19.3231 16.2256 19.3231 14.2769 18.1129 13.0872L18.0924 13.0667L10.7898 20.3692Z"
                        fill="#9045D6"
                      />
                      <path
                        id="Vector_4"
                        d="M14.6257 24.2667C14.6462 24.2667 14.6462 24.2667 14.6668 24.2667C16.3693 24.2667 17.7437 22.8923 17.7437 21.1898V17.0257C17.7437 15.3231 16.3693 13.9487 14.6668 13.9487C14.6462 13.9487 14.6462 13.9487 14.6257 13.9487V24.2667Z"
                        fill="#9045D6"
                      />
                    </g>
                    <path
                      id="Vector_5"
                      d="M29.6819 20.3692L29.6614 20.3897C28.4512 21.6 26.5025 21.6 25.3127 20.3897L22.3589 17.4359C21.1486 16.2256 21.1486 14.2769 22.3589 13.0872L22.3794 13.0667L29.6819 20.3692Z"
                      fill="#9045D6"
                    />
                    <path
                      id="Vector_6"
                      d="M30.8104 17.2923L30.7898 17.3128C29.5796 18.5231 27.6309 18.5231 26.4411 17.3128L23.4873 14.359C22.277 13.1487 22.277 11.2 23.4873 10.0103L23.5078 9.98975L30.8104 17.2923Z"
                      fill="#9045D6"
                    />
                    <path
                      id="Vector_7"
                      d="M9.70264 17.2923L9.72315 17.3128C10.9334 18.5231 12.8821 18.5231 14.0719 17.3128L17.0462 14.359C18.2565 13.1487 18.2565 11.2 17.0462 10.0103L17.0257 9.98975L9.70264 17.2923Z"
                      fill="#9045D6"
                    />
                    <path
                      id="Vector_8"
                      d="M25.8462 24.2667C25.8257 24.2667 25.8257 24.2667 25.8052 24.2667C24.1026 24.2667 22.7283 22.8923 22.7283 21.1898V17.0257C22.7283 15.3231 24.1026 13.9487 25.8052 13.9487C25.8257 13.9487 25.8257 13.9487 25.8462 13.9487V24.2667Z"
                      fill="#9045D6"
                    />
                    <g id="Group_3">
                      <path
                        id="Vector_9"
                        d="M14.5026 14.5641C15.0564 15.9179 14.8103 17.3128 13.9282 17.6615C13.0462 18.0307 11.8769 17.2307 11.3231 15.8769C10.7692 14.523 11.0154 13.1282 11.8975 12.7794C12.759 12.4307 13.9282 13.2307 14.5026 14.5641Z"
                        fill="#FFEFCF"
                      />
                      <path
                        id="Vector_10"
                        d="M25.9898 14.5641C25.436 15.9179 25.6821 17.3128 26.5642 17.6615C27.4463 18.0307 28.6155 17.2307 29.1693 15.8769C29.7232 14.523 29.477 13.1282 28.595 12.7794C27.7334 12.4307 26.5642 13.2307 25.9898 14.5641Z"
                        fill="#FFEFCF"
                      />
                    </g>
                    <path
                      id="Vector_11"
                      d="M20 39.9999C26.0923 39.9999 31.5282 37.2717 35.2 32.9845C33.4359 30.0101 27.3641 28.2255 23.4666 26.5024H16.9641C12.3282 28.5947 6.46152 30.2153 5.08716 33.3332C8.75895 37.4153 14.0718 39.9999 20 39.9999Z"
                      fill="#1A1887"
                    />
                    <path
                      id="Vector_12"
                      d="M17.0667 28.5538C17.0667 30.2974 18.482 31.7333 20.2461 31.7333C21.9897 31.7333 23.4256 30.318 23.4256 28.5538V21.1692C23.4256 19.4256 22.0102 17.9897 20.2461 17.9897C18.5025 17.9897 17.0667 19.4051 17.0667 21.1692V28.5538Z"
                      fill="#FFEFCF"
                    />
                    <path
                      id="Vector_13"
                      opacity="0.8"
                      d="M20.2461 18.9949C21.9897 18.9949 23.4256 20.4103 23.4256 22.1744V22.6256C22.0512 24.0821 20.2461 24.9641 18.2564 24.9641C17.8461 24.9641 17.4564 24.9231 17.0871 24.8615V22.1744C17.0666 20.4103 18.5025 18.9949 20.2461 18.9949Z"
                      fill="#FFEFCF"
                    />
                    <path
                      id="Vector_14"
                      d="M27.9794 13.9897C27.9794 19.0154 24.5128 22.9539 20.2461 22.9539C15.9794 22.9539 12.5128 19.0154 12.5128 13.9897C12.5128 8.96411 11.8358 2.97437 20.2461 2.97437C28.6564 2.97437 27.9794 8.94359 27.9794 13.9897Z"
                      fill="#FFEFCF"
                    />
                    <path
                      id="Vector_15"
                      d="M17.4768 27.6309L20.3691 31.6924L23.1589 27.6309L20.5742 39.3847L17.4768 27.6309Z"
                      fill="#1A1887"
                    />
                    <path
                      id="Vector_16"
                      d="M12.718 28.3693L15.3026 24.8616H17.477L20.4924 39.1385L12.718 28.3693Z"
                      fill="#1A1887"
                    />
                    <path
                      id="Vector_17"
                      d="M27.795 28.2667L25.1283 24.8616H23.0565L20.5745 39.3847L27.795 28.2667Z"
                      fill="#1A1887"
                    />
                    <path
                      id="Vector_18"
                      d="M25.8872 7.03589C25.8872 7.03589 27.6718 10.5641 27.8975 12.1231C28.1641 9.49743 25.8872 7.03589 25.8872 7.03589Z"
                      fill="#FFEFCF"
                    />
                    <path
                      id="Vector_19"
                      d="M18.3179 18.4819C18.3179 18.4819 19.1384 19.4255 20.2871 19.4255C21.4358 19.4255 22.2563 18.4819 22.2563 18.4819H18.3179Z"
                      fill="#FFEFCF"
                    />
                    <path
                      id="Vector_20"
                      d="M11.7539 13.7436C11.7744 13.7436 11.7744 13.7436 11.7949 13.7436C13.4975 13.7436 14.8719 12.3692 14.8719 10.6667V4.45127C14.8719 3.6923 13.1078 5.41538 11.7949 7.67179C11.7949 7.67179 11.7744 7.71281 11.7539 7.71281V13.7436Z"
                      fill="#9045D6"
                    />
                    <path
                      id="Vector_21"
                      d="M13.3948 5.94871V9.80512C13.4153 9.80512 13.4153 9.80512 13.4358 9.80512C15.2614 9.80512 16.7589 8.3282 16.7589 6.48204V3.03589C15.0563 3.65127 14.0307 4.67691 13.3948 5.94871Z"
                      fill="#9045D6"
                    />
                    <path
                      id="Vector_22"
                      d="M28.4102 13.7437C28.3897 13.7437 28.3897 13.7437 28.3692 13.7437C26.6666 13.7437 25.2922 12.3693 25.2922 10.6667V4.06162C25.2922 3.30264 27.0563 5.39495 28.3692 7.65136C28.3692 7.65136 28.3897 7.69239 28.4102 7.69239V13.7437Z"
                      fill="#9045D6"
                    />
                    <path
                      id="Vector_23"
                      d="M24.4308 4.96414C22.5642 4.96414 20.9847 3.63081 20.6359 1.84619H19.6513C17.518 1.84619 15.7949 3.56927 15.7949 5.7026C15.7949 7.83593 17.518 9.55901 19.6513 9.55901H26.2565C28.3898 9.55901 30.1129 7.83593 30.1129 5.7026C30.1129 5.43593 30.0924 5.18978 30.0308 4.94363H24.4308V4.96414Z"
                      fill="#9045D6"
                    />
                    <path
                      id="Vector_24"
                      d="M25.9691 4.73857C25.7845 5.55908 24.4102 5.96934 22.8512 5.64113C21.3127 5.31293 20.205 4.38985 20.3691 3.54882C20.5538 2.72831 21.9281 2.31805 23.4871 2.64626C25.0256 2.97446 26.1332 3.91805 25.9691 4.73857Z"
                      fill="#9045D6"
                    />
                  </g>
                </g>
                <defs>
                  <clipPath id="clip0_2558_120">
                    <rect width="40" height="40" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div className={css.userName}>
              {
                <p>
                  {currentUser?.attributes?.profile?.firstName}{' '}
                  {currentUser?.attributes?.profile?.lastName}
                </p>
              }
            </div>

            <div className={css.closeIcon} onClick={onClose}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="34"
                height="34"
                viewBox="0 0 34 34"
                fill="none"
              >
                <path d="M2 2L32 32M32 2L2 32" stroke="#A645D6" stroke-width="4"></path>
              </svg>
            </div>
          </div>
          <div className={css.innerBlockSide}>
            <div className={css.sideBarLogo}>
              <NamedLink className={css.logoLink} name="LandingPage">
                <IconLogo />
              </NamedLink>
            </div>
            <div className={css.url_link}>
              <button
                className="buy_button"
                onClick={() => {
                  history.push('/s');
                  window.location.reload();

                }}
              >
                Buy
              </button>
              <button
                Name="buy_button"
                onClick={() => {
                  history.push('/listings');
                  window.location.reload();

                }}
              >
                Sell
              </button>
              <button
                Name="buy_button"
                onClick={() => {
                  history.push('/learn');
                  window.location.reload();

                }}
              >
                Learn
              </button>
              {/* <div className={css.searchBar}>
                <input
                  type="text"
                  name="search"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                <svg
                  classNames={css.searhIcon}
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M17.1901 15.4782L23.6458 21.934C23.8727 22.1611 24.0001 22.469 24 22.79C23.9999 23.111 23.8723 23.4189 23.6452 23.6458C23.4181 23.8727 23.1102 24.0001 22.7892 24C22.4682 23.9999 22.1604 23.8723 21.9335 23.6452L15.4778 17.1893C13.5479 18.6841 11.1211 19.3875 8.69104 19.1565C6.26097 18.9255 4.01018 17.7773 2.39656 15.9457C0.782936 14.114 -0.0723147 11.7364 0.00479537 9.29655C0.0819054 6.85668 1.08558 4.53783 2.81165 2.81172C4.53771 1.08561 6.85651 0.0819075 9.29632 0.00479549C11.7361 -0.0723165 14.1137 0.782955 15.9453 2.39662C17.7769 4.01028 18.925 6.26113 19.156 8.69126C19.387 11.1214 18.6836 13.5483 17.1889 15.4782H17.1901ZM9.60045 16.7993C11.5099 16.7993 13.3412 16.0408 14.6914 14.6905C16.0416 13.3403 16.8001 11.509 16.8001 9.59949C16.8001 7.68997 16.0416 5.85866 14.6914 4.50843C13.3412 3.15819 11.5099 2.39964 9.60045 2.39964C7.69098 2.39964 5.85971 3.15819 4.50951 4.50843C3.15932 5.85866 2.40078 7.68997 2.40078 9.59949C2.40078 11.509 3.15932 13.3403 4.50951 14.6905C5.85971 16.0408 7.69098 16.7993 9.60045 16.7993Z"
                    fill="#0EC3A8"
                  />
                </svg>
              </div> */}
            </div>
            <div className={css.site_link}>
              <div className="linkedin">
                <a href="https://www.linkedin.com/@Sharetribe?_l=en_US">
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16">
                    <path d="M13.938.188H1.184C.6.188.125.667.125 1.26v12.73c0 .591.475 1.072 1.06 1.072h12.753A1.07 1.07 0 0 0 15 13.99V1.26A1.07 1.07 0 0 0 13.938.187ZM4.62 12.938H2.416v-7.1h2.208v7.1h-.003Zm-1.102-8.07a1.279 1.279 0 1 1 .001-2.558 1.279 1.279 0 0 1 0 2.558Zm9.367 8.069H10.68V9.483c0-.823-.016-1.882-1.145-1.882-1.15 0-1.325.896-1.325 1.823v3.512H6.005V5.84H8.12v.97h.03c.296-.559 1.016-1.146 2.089-1.146 2.23 0 2.646 1.47 2.646 3.383v3.892Z" />
                  </svg>
                </a>
              </div>
              <div className="xicon">
                <a href="https://x.com/sharetribe">
                  <svg width="16.61" height="14.999" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.641 0h2.453L9.709 6.131 16 14.448h-4.937L7.198 9.394l-4.426 5.054H.32l5.705-6.557L0 0h5.06l3.492 4.617L12.642 0Zm-.858 13.009h1.36L4.344 1.386h-1.46l8.898 11.623Z" />
                  </svg>
                </a>
              </div>
              <div className="facebook">
                <a href="https://www.facebook.com/Sharetribe/">
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="18">
                    <path d="m8.643 9.688.472-3.077H6.163V4.614c0-.841.413-1.662 1.735-1.662H9.24V.332S8.022.126 6.858.126c-2.431 0-4.02 1.474-4.02 4.141v2.345H.134v3.077h2.702v7.437h3.326V9.687h2.48Z" />
                  </svg>
                </a>
              </div>
              <div className="instagram">
                <a href="https://www.instagram.com/">
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16">
                    <path d="M7.566 3.807A3.809 3.809 0 0 0 3.75 7.622a3.809 3.809 0 0 0 3.815 3.815 3.809 3.809 0 0 0 3.815-3.815 3.809 3.809 0 0 0-3.815-3.815Zm0 6.295a2.485 2.485 0 0 1-2.48-2.48 2.483 2.483 0 0 1 2.48-2.48 2.483 2.483 0 0 1 2.48 2.48 2.485 2.485 0 0 1-2.48 2.48Zm4.86-6.451a.89.89 0 1 0-1.779 0 .89.89 0 0 0 1.78 0Zm2.528.903c-.057-1.192-.33-2.248-1.202-3.118-.87-.87-1.926-1.142-3.118-1.202-1.229-.07-4.911-.07-6.14 0C3.307.29 2.25.563 1.378 1.433.504 2.303.235 3.358.175 4.55c-.07 1.229-.07 4.911 0 6.14.056 1.192.329 2.247 1.202 3.117.873.87 1.926 1.143 3.118 1.202 1.228.07 4.91.07 6.139 0 1.192-.056 2.248-.328 3.118-1.202.87-.87 1.142-1.925 1.202-3.117.07-1.229.07-4.908 0-6.136Zm-1.588 7.454a2.511 2.511 0 0 1-1.414 1.414c-.98.389-3.304.3-4.386.3-1.083 0-3.41.085-4.386-.3a2.511 2.511 0 0 1-1.415-1.414c-.388-.98-.299-3.304-.299-4.386 0-1.083-.086-3.41.3-4.386A2.507 2.507 0 0 1 3.18 1.821c.98-.388 3.303-.299 4.386-.299 1.082 0 3.41-.086 4.386.3.65.258 1.152.76 1.414 1.414.389.979.3 3.303.3 4.386 0 1.082.089 3.41-.3 4.386Z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
